<div class="flex flex-col h-full w-full">
  <app-page-header>
    @if (isMixedReport) {
      <!-- Gemischter Lauf: die Regelset-Umschalter stehen laut Entwurf an der Stelle des Titels.
           Bewusst dieselbe Button-Komponente wie die Aktionen rechts – im Figma-Entwurf sind es
           Instanzen derselben Komponente, aktiv = gefüllt, inaktiv = weiß mit Rand. -->
      <div class="flex flex-row items-center gap-4 min-w-0 overflow-x-auto">
        @for (entry of mixedEntries; track entry.rulesetId ?? entry.rulesetName; let i = $index) {
          <bic-button (onClick)="selectMixedEntry(i)"
                      [buttonType]="i === activeMixedIndex ? 'primary' : 'white'"
                      [tooltip]="mixedEntryTypeLabel(entry)">
            <fa-icon [icon]="mixedEntryIcon(entry)" iconLeft></fa-icon>
            {{ entry.rulesetName }}
          </bic-button>
        }
      </div>
    } @else {
      <div>Prüfbericht</div>
    }

    <!-- Aktions-Buttons in der Topbar (aus dem Overview-Banner hierher verschoben, bessere Responsiveness).
         „Öffnen" (Viewer) ist nur für Prüftypen mit GUID-Listen angebunden (siehe supportsOpenInViewer). -->
    <div class="flex flex-row items-center gap-4">
      <!-- Aufklappen / Einklappen Button nur in der Kartenansicht anzeigen -->
      @if (selectedViewMode === 'list') {
        <bic-button (click)="toggleAllCards()" buttonType="white" color="#FFF">
          {{ allCardsExpanded ? 'Einklappen' : 'Aufklappen' }}
          <fa-icon [icon]="allCardsExpanded ? faCollapseIcon : faExpandIcon" iconLeft></fa-icon>
        </bic-button>
      }

      <bic-button (onClick)="toggleViewMode()" buttonType="white" color="#FFF" tooltip="Ansicht wechseln">
        Ansicht
        <fa-icon [icon]="selectedViewMode === 'column' ? faTable : faList" iconLeft></fa-icon>
      </bic-button>

      <!-- Filter – wirkt in beiden Ansichten (gemeinsamer ReportFilterState) -->
      <app-bic-filter (filtersChange)="onFiltersChange($event)" [filtersConfig]="reportFilterSections">
      </app-bic-filter>

      <div>
        <bic-button [bicDropdownTrigger]="downloadMenu"
                    [disabled]="!report || downloadingReport"
                    buttonType="white" color="#FFF" tooltip="Bericht herunterladen">
          Bericht
          <fa-icon [icon]="faArrowDownToBracket" iconLeft></fa-icon>
        </bic-button>
        <bic-dropdown #downloadMenu [dropdownPlacement]="'bottom-end'">
          <bic-dropdown-element (onClick)="downloadReportAsExcel()">
            <fa-icon [icon]="faFileExcel" iconLeft></fa-icon>
            Als Excel (.xlsx)
          </bic-dropdown-element>
          <bic-dropdown-element (onClick)="downloadReportAsPdf()">
            <fa-icon [icon]="faFilePdf" iconLeft></fa-icon>
            Als PDF (.pdf)
          </bic-dropdown-element>
        </bic-dropdown>
      </div>
      <bic-button (click)="openInViewerPressed()" [disabled]="!activateOpenInViewer" buttonType="white" color="#FFF" tooltip="Im Viewer öffnen">
        Öffnen
        <fa-icon [icon]="faArrowUpRightFromSquare" iconLeft></fa-icon>
      </bic-button>
      <bic-button (click)="viewLogs()" [disabled]="!currentInstanceId" buttonType="white" color="#FFF">
        Logs
        <fa-icon [icon]="faClipboardList" iconLeft></fa-icon>
      </bic-button>
    </div>
  </app-page-header>

  <div class="flex flex-col w-full flex-1 min-h-0 gap-6 p-6">
    @if (loading) {
      <div class="flex flex-col flex-1 h-full justify-center items-center py-16">
        <div class="flex flex-row items-center gap-2">
          <fa-icon [icon]="faSpinnerThird" class="text-gray-500 animate-spin text-svg-16"></fa-icon>
          <span class="text-sm font-medium text-gray-500">Bericht wird geladen...</span>
        </div>
      </div>
    } @else if (report || isMixedReport) {
      <!-- Die Regelset-Umschalter stehen in der Kopfzeile (siehe oben) – laut Entwurf an der
           Stelle des Titels, nicht über dem Overview. -->
      @if (!report) {
        <div class="flex flex-col flex-1 h-full justify-center items-center py-16 gap-2">
          <span class="text-sm font-medium text-gray-500">Für dieses myCheck liegt kein Bericht vor.</span>
          @if (mixedEntries[activeMixedIndex]?.error) {
            <span class="text-xs text-gray-500">{{ mixedEntries[activeMixedIndex].error }}</span>
          }
        </div>
      } @else {
      <!-- Overview Header -->
      <app-overview-reports
        [activateDownload]="false"
        [checkerName]="report.meta.checkerName"
        [completedValue]="statusSummary.completed"
        [warningValue]="statusSummary.warning"
        [errorValue]="statusSummary.error"
        [singleResult]="isIdsReport ? idsSingleResult : null"
        [date]="report.meta.checkDate"
        [fileDeactivated]="true"
        [fileCount]="report.meta.fileCount"
        [fileName]="report.meta.fileName"
        [info1]="runDisplayName"
        [info1Tooltip]="runDisplayNameTooltip"
        [info2]="rulesetDisplayName"
        [ruleTypeLabel]="checkRuleTypeLabel"
        [rules]="'' + overviewRuleCount"
        [checkType]="'MY_CHECK'"
        [showActions]="false"
        [myCheckLink]="myCheckRouterLink"
        [rulesetLinks]="rulesetRouterLinks"
        (showLogs)="viewLogs()">
      </app-overview-reports>

      <!-- Kein bewertbares Ergebnis (IDS "nicht anwendbar"): Ursache nennen, statt die
           Prozentkreise auf 0 % stehen zu lassen, als wäre nichts erfüllt. Dieser Lauf
           endete früher als Fehlschlag und hatte gar keinen Bericht. -->
      @if (idsResultInfo) {
        <div class="mt-4 flex flex-row items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <fa-icon [icon]="faCircleInfo" class="text-base shrink-0 text-gray-400 mt-0.5"></fa-icon>
          <p class="text-sm text-gray-700">{{ idsResultInfo }}</p>
        </div>
      }

      <!-- Ergebnis-View Container -->
      <div class="flex w-full flex-1 min-h-0 overflow-hidden">

      <!-- FALL A: Kartenansicht (gilt für ALLE Berichtsarten – KI, Hybrid und IDS) -->
        @if (selectedViewMode === 'list') {
          <app-report-card-view
            (toggleItem)="toggleCard($event)"
            (taskClicked)="createTaskForCardFacet($event)"
            [expandedKeys]="expandedCardKeys"
            [model]="cardModel">
          </app-report-card-view>
        }
        @else {

          <!-- B1: Normaler / Hybrid / KI Prüfbericht (Spalten) -->
          @if (!isIdsReport) {
            <bic-result>
              <!-- Nicht-Detail-Spalten -->
              @for (levelKey of renderedColumnKeys; track levelKey) {
                <bic-result-column [selectedElement]="columnSelection[levelKey]"
                                   (selectedElementChange)="selectColumnItem(levelKey, $event)"
                                   [alwaysFixed]="true"
                                   [style.flex]="columnFlex(levelKey) + ' 1 0%'"
                                   [style.max-width]="'100%'" [style.min-width]="'0'">
                  <bic-result-header header>
                    <!-- Spacer links/rechts gleichen Status-Icon und Task-Button-Feld der Zeilen aus,
                         sonst laufen Header und Werte auseinander. -->
                    <fa-icon [icon]="faCircleCheck" aria-hidden="true" class="invisible text-base shrink-0"></fa-icon>
                    <div class="grid items-center gap-4 flex-1 min-w-0"
                         [style.grid-template-columns]="columnGridTemplate(levelKey)">
                      @for (colKey of orderedColumnKeys(levelKey); track colKey) {
                        @if (colKey === identifyingColumnKey(levelKey)) {
                          <div class="min-w-0 truncate">{{ columnHeaderLabel(levelKey) }}</div>
                        } @else {
                          <div [class.text-right]="colKey === 'rating'"
                               [class.min-w-0]="colKey === 'description'" [class.truncate]="colKey === 'description'"
                               class="whitespace-nowrap">{{ secondaryColumnLabel(levelKey, colKey) }}</div>
                        }
                      }
                    </div>
                    <div aria-hidden="true" class="w-4 shrink-0"></div>
                  </bic-result-header>
                  @for (item of columnItems(levelKey); track columnItemTrackKey(item)) {
                    <!-- Eigenes Status-Icon statt [status]-Input: die Library kennt nur
                         success/warning/error, 'unknown' (z. B. nicht relevante Datei/fehlende
                         Information) braucht ihr eigenes (graues) Icon. -->
                    <bic-result-row (taskClicked)="createTaskForReportItem(levelKey, item)"
                                    [value]="item.name">
                      <fa-icon [icon]="statusIcon(item.status)" [class]="statusColor(item.status)"
                               class="text-base shrink-0"></fa-icon>
                      <div class="grid items-center gap-4 w-full min-w-0"
                           [style.grid-template-columns]="columnGridTemplate(levelKey)">
                        @for (colKey of orderedColumnKeys(levelKey); track colKey) {
                          @if (colKey === identifyingColumnKey(levelKey)) {
                            <div [tooltip]="item.name" [tooltipWidth]="400"
                                 class="min-w-0 truncate">{{ item.name }}</div>
                          } @else {
                            <div class="whitespace-nowrap text-sm"
                                 [class.text-gray-900]="colKey === 'rating'" [class.text-gray-400]="colKey !== 'rating'"
                                 [class.text-right]="colKey === 'rating'"
                                 [class.min-w-0]="colKey === 'description'" [class.truncate]="colKey === 'description'"
                                 [tooltip]="colKey === 'description' ? columnItemValue(levelKey, item, colKey) : null">
                              {{ columnItemValue(levelKey, item, colKey) }}
                            </div>
                          }
                        }
                      </div>
                    </bic-result-row>
                  }
                </bic-result-column>
              }

              <!-- Ergebnis-Spalte (Letzte Spalte: Details) -->
              <bic-result-column #detailResultColumn emptyText="Bitte auswählen"
                                 [alwaysFixed]="true"
                                 [style.flex]="columnFlex('detail') + ' 1 0%'"
                                 [style.max-width]="'100%'" [style.min-width]="'0'">
                <bic-result-header header>
                  <div class="w-full flex flex-row justify-between min-w-0">
                    <div class="flex-1">{{ detailHeaderLabel }}</div>
                  </div>
                </bic-result-header>
                @for (step of detailBlocks.steps; track $index) {
                  @let detail = step.detail;
                  <!-- Eigenes Icon/Task-Button statt Library-Defaults, damit alles innerhalb des
                       stopPropagation()-geschützten Bereichs liegt (siehe suppressDetailRowSelection). -->
                  @if (step.isFacet) {
                    <!-- Facetten-Zeile: ein einzelnes, strukturiertes Ergebnis (Name, Beschreibung,
                         Bauteilzahl, Prozent) statt eines Prüfschritt-Kastens mit Ausgabefeldern.
                         Trägt die Regelergebnisse des BIM-Basis-Checks innerhalb einer Hybrid-
                         Prüfung; die Kartenansicht rendert dieselben Details als 'facet'-Block. -->
                    <bic-result-row [disableTasks]="true" appearance="'card'" class="group">
                      <div (click)="$event.stopPropagation()" class="flex flex-row items-center gap-3 min-w-0 flex-1">
                        @if (showDetailStatus) {
                          <fa-icon [class]="statusColor(detail.type)" [icon]="statusIcon(detail.type)"
                                   class="text-base shrink-0"></fa-icon>
                        }
                        <div class="flex flex-col gap-1 min-w-0 flex-1">
                          <div class="text-sm font-medium text-gray-900 break-words">
                            {{ detail.name || detail.facetLabel || detail.text || '–' }}
                          </div>
                          @if (detail.valueChips?.length) {
                            <div class="text-sm text-gray-600 break-words">{{ detail.valueChips![0].value }}</div>
                          }
                          @if (detail.count > 0) {
                            <div class="text-xs text-gray-500">{{ detail.count }} Bauteile</div>
                          }
                        </div>
                        <div class="w-16 shrink-0 text-right text-sm text-gray-900">{{ percentLabel(detail.percent) }}
                        </div>
                        <app-report-row-actions (copyClick)="copyDetailResponse(step)"
                                                 (taskClick)="createTaskForReportDetail(detail)">
                        </app-report-row-actions>
                      </div>
                    </bic-result-row>
                  } @else {
                  <bic-result-row [disableTasks]="true" [lineBreaksDisabled]="false"
                                  appearance="'card'" class="group">
                    <div (click)="$event.stopPropagation()" class="flex flex-row gap-3 min-w-0 flex-1">
                      @if (showDetailStatus) {
                        <fa-icon [icon]="statusIcon(detail.type)" [class]="statusColor(detail.type)"
                                 class="text-base shrink-0 mt-0.5"></fa-icon>
                      }
                      <div appReportSourceLinks class="flex flex-col gap-2 min-w-0 flex-1">
                        @if (detail.heading) {
                          <div class="text-base font-medium text-gray-700">{{ detail.heading }}</div>
                        }
                        @for (entry of step.entries; track $index) {
                          <div class="text-sm min-w-0 break-words">
                            @if (entry.label) {
                              <div class="font-medium text-gray-700">{{ entry.label }}</div>
                            }
                            <markdown
                              [data]="entry.value | reportSourceLinks:detail.sourceFiles:sourceLinkProjectId | normalizeMarkdown"
                              class="text-gray-600 report-answer-markdown report-answer-text"></markdown>
                          </div>
                        }
                        @if (showRawOutput && detail.rawOutput) {
                          <pre
                            class="text-xs bg-gray-50 border rounded p-3 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-words font-mono text-gray-700">{{ detail.rawOutput | json }}</pre>
                        }
                      </div>
                      <app-report-row-actions (copyClick)="copyDetailResponse(step)"
                                               (taskClick)="createTaskForReportDetail(detail)">
                      </app-report-row-actions>
                    </div>
                  </bic-result-row>
                  }
                }
                @if (constantDetailEntries.length > 0) {
                  <bic-result-row [disableTasks]="true" appearance="'card'">
                    <div (click)="$event.stopPropagation()" class="flex flex-row gap-3 min-w-0 flex-1">
                      <fa-icon [icon]="faCircleInfo" class="text-base shrink-0 mt-0.5 text-gray-400"></fa-icon>
                      <div class="flex flex-col gap-2 min-w-0 flex-1">
                        <div class="text-base font-medium text-gray-700">Regelparameter</div>
                        @for (entry of constantDetailEntries; track entry.label) {
                          <div class="text-sm min-w-0 break-words">
                            <div class="font-medium text-gray-700">{{ entry.label }}</div>
                            <markdown [data]="entry.value | normalizeMarkdown"
                                      class="text-gray-600 report-answer-markdown report-answer-text"></markdown>
                          </div>
                        }
                      </div>
                    </div>
                  </bic-result-row>
                }
              </bic-result-column>
            </bic-result>
          }
          @else {
            <bic-result>
              @for (idsLevelKey of idsOrderedLevelKeys; track idsLevelKey) {
                @switch (idsLevelKey) {
                  @case ('files') {
                    <bic-result-column #groupsColumn
                                       (selectedElementChange)="onIdsGroupSelected()"
                                       [(selectedElement)]="selectedGroup"
                                       [style.flex]="idsColumnWidths['files'] ? idsColumnWidths['files'] + ' 1 0%' : null"
                                       [style.max-width]="idsColumnWidths['files'] != null ? '100%' : null"
                                       [style.min-width]="idsColumnWidths['files'] != null ? '0' : null"
                                       emptyText="Keine Dateien">
                      <bic-result-header header>
                        <fa-icon [icon]="faCircleCheck" aria-hidden="true"
                                 class="invisible text-base shrink-0"></fa-icon>
                        <div class="flex-1 flex flex-row justify-between min-w-0">
                          <div>{{ filesHeaderLabel }}</div>
                          @if (showFileResultColumn) {
                            <div>Ergebnis</div>
                          }
                        </div>
                        <!-- Platzhalter für die Actions-Spalte der Zeilen (2 Buttons + Abstand): ohne
                             ihn steht "Ergebnis" hier nicht unter den Werten, weil nur die Zeilen die
                             Spalte zeigen. -->
                        <div aria-hidden="true" class="w-3.5 shrink-0"></div>
                      </bic-result-header>
                      @for (group of idsFiles; track group.name) {
                        <bic-result-row [disableTasks]="true" [status]="group.status" [value]="group" class="group">
                          <div class="flex flex-row flex-1 gap-4 min-w-0">
                            <div class="truncate">{{ group.name }}</div>
                          </div>
                          @if (showFileResultColumn) {
                            <div class="flex flex-row justify-end gap-4 flex-shrink-0">
                              {{ percentLabel(group.percent) }}
                            </div>
                          }
                          <app-report-row-actions (copyClick)="copyIdsFileSummary(group)"
                                                   (taskClick)="createTaskForIdsFile(group)">
                          </app-report-row-actions>
                        </bic-result-row>
                      }
                    </bic-result-column>
                  }
                  @case ('specifications') {
                    <bic-result-column [(selectedElement)]="selectedRule"
                                       [style.flex]="idsColumnWidths['specifications'] ? idsColumnWidths['specifications'] + ' 1 0%' : null"
                                       [style.max-width]="idsColumnWidths['specifications'] != null ? '100%' : null"
                                       [style.min-width]="idsColumnWidths['specifications'] != null ? '0' : null"
                                       emptyText="Keine Spezifikationen">
                      <bic-result-header header>
                        @if (showSpecStatus) {
                          <fa-icon [icon]="faCircleCheck" aria-hidden="true"
                                   class="invisible text-base shrink-0"></fa-icon>
                        }
                        <div
                          class="flex-1 flex flex-row items-center gap-4 min-w-0 text-xs font-medium text-gray-500 uppercase">
                          @for (colKey of idsSpecColumnKeys; track colKey) {
                            @switch (colKey) {
                              @case ('name') {
                                <div class="flex-1 min-w-0">{{ idsSpecificationsHeaderLabel }}</div>
                              }
                              @case ('applicability') {
                                <div class="w-40 flex-shrink-0">Anwendbarkeit</div>
                              }
                              @case ('elements') {
                                <div class="w-16 flex-shrink-0 text-right">Bauteile</div>
                              }
                              @case ('result') {
                                <div class="w-16 flex-shrink-0 text-right">Ergebnis</div>
                              }
                            }
                          }
                        </div>
                        <!-- Platzhalter für die Actions-Spalte der Zeilen (2 Buttons + Abstand): ohne
                             ihn steht "Ergebnis" hier nicht unter den Werten, weil nur die Zeilen die
                             Spalte zeigen. -->
                        <div aria-hidden="true" class="w-3.5 shrink-0"></div>
                      </bic-result-header>
                      @for (spec of idsSpecifications; track idsSpecificationTrackKey(spec)) {
                        <bic-result-row [disableTasks]="true" [status]="showSpecStatus ? spec.status : undefined"
                                        [value]="spec" class="group">
                          <div class="flex flex-row items-center gap-4 flex-1 min-w-0">
                            @for (colKey of idsSpecColumnKeys; track colKey) {
                              @switch (colKey) {
                                @case ('name') {
                                  <div class="flex-1 min-w-0 truncate font-medium">{{ spec.name }}</div>
                                }
                                @case ('applicability') {
                                  <div class="w-40 flex-shrink-0 flex flex-row items-center gap-1 min-w-0">
                                    @if (spec.applicabilities && spec.applicabilities.length > 0) {
                                      <bic-badge [tooltip]="specApplicabilityTooltip()"
                                                 [tooltipSubtext]="specApplicabilityTooltipSubtext(spec)"
                                                 [tooltipWidth]="320"
                                                 class="cursor-help">{{ chipText(spec.applicabilities[0]) }}
                                      </bic-badge>
                                      @if (chipExtraCount(spec.applicabilities, 1) > 0) {
                                        <span
                                          class="text-xs text-gray-500 flex-shrink-0">+{{ chipExtraCount(spec.applicabilities, 1) }}</span>
                                      }
                                    } @else {
                                      <span class="text-xs text-gray-400">–</span>
                                    }
                                  </div>
                                }
                                @case ('elements') {
                                  <div class="w-16 flex-shrink-0 text-right text-sm">{{ spec.elementCount ?? 0 }}</div>
                                }
                                @case ('result') {
                                  <div class="w-16 flex-shrink-0 text-right text-sm font-medium">{{ spec.percent ?? 0 }}%
                                  </div>
                                }
                              }
                            }
                            <app-report-row-actions (copyClick)="copyIdsSpecificationSummary(spec)"
                                                     (taskClick)="createTaskForIdsSpecification(spec)">
                            </app-report-row-actions>
                          </div>
                        </bic-result-row>
                      }
                    </bic-result-column>
                  }
                  @case ('requirements') {
                    <bic-result-column emptyText="Bitte Spezifikation auswählen"
                                       [style.flex]="idsColumnWidths['requirements'] ? idsColumnWidths['requirements'] + ' 1 0%' : null"
                                       [style.max-width]="idsColumnWidths['requirements'] != null ? '100%' : null"
                                       [style.min-width]="idsColumnWidths['requirements'] != null ? '0' : null">
                      <bic-result-header header>
                        <!-- Unsichtbarer Spacer: mit appearance="card" zeigt jede Zeile immer ein Icon
                             (farbig bei Status, sonst neutrales Info-Icon) - der Header braucht ihn
                             daher unabhängig von showReqStatus, sonst laufen Header und Zeilen auseinander. -->
                        <fa-icon [icon]="faCircleCheck" aria-hidden="true"
                                 class="invisible text-base shrink-0"></fa-icon>
                        <div class="flex-1 flex flex-row items-center gap-4 min-w-0 text-xs font-medium text-gray-500 uppercase">
                          <div class="flex-1 min-w-0">Facette</div>
                          <div class="flex-1 min-w-0">Name</div>
                          <div class="w-16 flex-shrink-0 text-right">Ergebnis</div>
                          <!-- Platzhalter für die Actions-Spalte der Zeilen (2 Buttons + Abstand): auf
                               derselben Verschachtelungsebene wie in der Zeile (dort steckt
                               app-report-row-actions ebenfalls in dieser Facette/Name/Ergebnis-Zeile,
                               nicht eine Ebene höher) - sonst stimmt "Ergebnis" nicht mit den Werten
                               überein. -->
                          <div aria-hidden="true" class="w-3.5 shrink-0"></div>
                        </div>
                      </bic-result-header>
                      @for (req of idsRequirements; track idsRequirementTrackKey(req)) {
                        <!-- appearance="card" statt grouped: jede Anforderung bekommt wie im KI-Bericht
                             ihre eigene Karte, die sich an ihren Inhalt anpasst, statt in einer einzigen,
                             auf volle Spaltenhöhe gestreckten Box zu stecken.
                             Bewusst OHNE eckige Klammern (wie bei den KI-Detailkarten/Regelparameter-Karte):
                             mit [appearance]="'card'" (echtes Binding) rendert die Library bei fehlendem
                             [status] zusätzlich ihr eigenes neutrales Info-Icon vor dem ng-content - zusammen
                             mit unserem eigenen Icon unten wären das zwei Icons pro Zeile. Ohne Klammern bleibt
                             "appearance" der literale String "'card'" (inkl. Anführungszeichen) und ist nie
                             gleich 'card', die Library rendert also keins ihrer eigenen Icons.
                             Kein [status]-Input hier (bewusst wie bei den KI-Detailkarten): der Status-Icon-
                             Slot der Library liegt AUSSERHALB des ng-content und damit außerhalb des unten
                             stehenden stopPropagation()-Bereichs - ein Klick genau auf dieses Icon würde die
                             Spalte trotzdem erreichen und die Zeile über bindRowHandlers() der Library als
                             "selected" markieren (Requirements sind reine Anzeige, kein Auswahl-Ziel wie die
                             Spezifikationen). Das Status-Icon wird daher selbst gerendert, innerhalb des
                             geschützten Bereichs. -->
                        <bic-result-row [disableTasks]="true" appearance="'card'" class="group">
                          <div (click)="$event.stopPropagation()" class="flex-1 flex flex-col gap-1 min-w-0">
                            <div class="flex flex-row items-center gap-3 min-w-0">
                              @if (showReqStatus) {
                                <fa-icon [icon]="statusIcon(req.type)" [class]="statusColor(req.type)"
                                         class="text-base shrink-0"></fa-icon>
                              } @else {
                                <fa-icon [icon]="faCircleInfo" class="text-base shrink-0 text-gray-400"></fa-icon>
                              }
                              <div class="flex-1 flex flex-row gap-4 min-w-0">
                                <div class="flex-1 min-w-0 truncate text-sm font-medium text-gray-900">{{ req.facetLabel || '–' }}</div>
                                <div class="flex-1 min-w-0 flex flex-row items-center gap-2">
                                  <bic-badge [tooltip]="facetTooltip()" [tooltipSubtext]="facetTooltipSubtext(req)"
                                             [tooltipWidth]="320"
                                             class="truncate cursor-help">{{ req.name || '–' }}
                                  </bic-badge>
                                </div>
                                <div class="w-16 flex-shrink-0 text-right text-sm text-gray-900">{{ req.percent ?? 0 }} %</div>
                                <app-report-row-actions (copyClick)="copyIdsRequirementSummary(req)"
                                                         (taskClick)="createTaskForIdsRequirement(req)">
                                </app-report-row-actions>
                              </div>
                            </div>
                            <!-- Warum die Anforderung gerissen ist. Fehlt bei erfuellten Anforderungen und
                                 bei Berichten von vor dieser Erweiterung - dann bleibt die Karte einzeilig
                                 wie bisher. -->
                            @if (req.failureReasons && req.failureReasons.length > 0) {
                              <div class="flex flex-col gap-0.5 pl-7">
                                @for (reason of req.failureReasons; track reason.type) {
                                  <span class="text-xs text-gray-500">{{ failureReasonText(reason) }}</span>
                                }
                              </div>
                            }
                          </div>
                        </bic-result-row>
                      }
                    </bic-result-column>
                  }
                }
              }
            </bic-result>
          }
        }

      </div>
      }
    } @else {
      <div class="flex flex-col flex-1 justify-center items-center">
        <span class="text-sm text-gray-500">Kein Bericht gefunden.</span>
      </div>
    }
  </div>
</div>
